import { SkillMetadata } from "@models";
import { captureSentryException } from "@utils";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChatSkillContext } from "src/components/Chat/types";
import { useGetSkills } from "src/hooks";
import { useCurrentAgentThread } from "src/hooks/useAgentThreads";
import { useStore } from "src/stores";

export const buildCreateSkillFromChatAgentPrompt = () =>
  [
    "I want to create a reusable skill from the current chat.",
    "",
    "Visibility: PRIVATE unless this chat explicitly asks for organization visibility",
    "",
    "Use the conversation above as source material, but leave out this prompt and any earlier skill-draft instructions. Return an editable draft_skill card, and wait for me to click Apply before creating or changing any skill.",
  ].join("\n");

const metadataStringIds = (value: unknown) =>
  Array.isArray(value) ? value.map(String) : null;

const areStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const skillMetadataSyncSignature = ({
  threadId,
  attachedSkillIds,
}: {
  threadId: string;
  attachedSkillIds: string[];
}) =>
  JSON.stringify({
    threadId,
    attachedSkillIds,
  });

const escapeAttachedSkillValue = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const buildSkillContextsPromptContext = (skills: ChatSkillContext[]) => {
  if (skills.length === 0) return "";

  return [
    "<attached-skills>",
    "These skills are attached to this message. Read each listed SKILL.md with read_file before using tools or answering.",
    ...skills.map((skill) =>
      [
        "<skill>",
        `<id>${escapeAttachedSkillValue(skill.id)}</id>`,
        `<name>${escapeAttachedSkillValue(skill.name)}</name>`,
        `<path>${escapeAttachedSkillValue(skill.path)}</path>`,
        "</skill>",
      ].join("\n")
    ),
    "</attached-skills>",
  ].join("\n");
};

export const buildAttachedSkillsPromptContext = (skills: SkillMetadata[]) => {
  const attachableSkills = skills.filter(
    (skill) => skill.enabled && !skill.forceEnabled
  );
  if (attachableSkills.length === 0) {
    return "";
  }

  return [
    "<attached-skills>",
    "These skills are explicitly attached to this run. Read each listed SKILL.md with read_file before producing the final answer, and apply the skill instructions to the final visible answer.",
    ...attachableSkills.map((skill) =>
      [
        "<skill>",
        `<id>${escapeAttachedSkillValue(skill.id)}</id>`,
        `<name>${escapeAttachedSkillValue(skill.name)}</name>`,
        `<path>/skills/user/${escapeAttachedSkillValue(skill.name)}/SKILL.md</path>`,
        "</skill>",
      ].join("\n")
    ),
    "</attached-skills>",
  ].join("\n");
};

export const useChatSkillRuntime = ({
  orgId,
  isLoading,
  setInput,
  setShouldAutoSubmit,
}: {
  orgId: string;
  isLoading: boolean;
  setInput: Dispatch<SetStateAction<string>>;
  setShouldAutoSubmit: Dispatch<SetStateAction<boolean>>;
}) => {
  const { agentStore } = useStore();
  const [attachedSkills, setAttachedSkills] = useState<SkillMetadata[]>([]);
  const hasUserChangedAttachedSkillsRef = useRef(false);
  const lastAttemptedSkillMetadataSyncRef = useRef<string | null>(null);
  const { data: availableSkills = [] } = useGetSkills({
    orgId,
    enabled: !!orgId,
  });
  const { data: currentThread } = useCurrentAgentThread();
  const currentThreadMetadata = currentThread?.metadata as
    | Record<string, unknown>
    | undefined;
  const metadataAttachedSkillIds = useMemo(
    () => metadataStringIds(currentThreadMetadata?.attachedSkillIds),
    [currentThreadMetadata]
  );

  const handleAttachedSkillsChange = useCallback((skills: SkillMetadata[]) => {
    hasUserChangedAttachedSkillsRef.current = true;
    setAttachedSkills(skills);
  }, []);

  const resetAttachedSkillsForThreadChange = useCallback(() => {
    hasUserChangedAttachedSkillsRef.current = false;
    lastAttemptedSkillMetadataSyncRef.current = null;
    setAttachedSkills([]);
  }, []);

  useEffect(() => {
    if (
      hasUserChangedAttachedSkillsRef.current ||
      (attachedSkills.length > 0 && metadataAttachedSkillIds === null)
    ) {
      return;
    }

    const hydratedSkills = (metadataAttachedSkillIds ?? [])
      .map((skillId) =>
        availableSkills.find((skill) => skill.id === String(skillId))
      )
      .filter(
        (skill): skill is SkillMetadata =>
          skill !== undefined && skill.enabled && !skill.forceEnabled
      );

    setAttachedSkills((current) => {
      return areStringArraysEqual(
        current.map((skill) => skill.id),
        hydratedSkills.map((skill) => skill.id)
      )
        ? current
        : hydratedSkills;
    });
  }, [attachedSkills.length, availableSkills, metadataAttachedSkillIds]);

  useEffect(() => {
    const threadId = agentStore.currentThreadId;
    if (!threadId) {
      return;
    }

    const nextIds = attachedSkills.map((skill) => skill.id);
    const existingIds = metadataAttachedSkillIds ?? [];

    if (
      !hasUserChangedAttachedSkillsRef.current &&
      attachedSkills.length === 0 &&
      existingIds.length > 0
    ) {
      return;
    }

    if (areStringArraysEqual(nextIds, existingIds)) {
      return;
    }

    const nextSyncSignature = skillMetadataSyncSignature({
      threadId,
      attachedSkillIds: nextIds,
    });
    if (lastAttemptedSkillMetadataSyncRef.current === nextSyncSignature) {
      return;
    }

    const nextMetadata: { name: string } & Record<string, unknown> = {
      ...(currentThreadMetadata ?? {}),
      name:
        typeof currentThreadMetadata?.name === "string"
          ? currentThreadMetadata.name
          : "Untitled",
      attachedSkillIds: nextIds,
    };
    delete nextMetadata.excludedSkillIds;

    lastAttemptedSkillMetadataSyncRef.current = nextSyncSignature;
    agentStore.updateThread(threadId, nextMetadata).catch((caught) => {
      if (lastAttemptedSkillMetadataSyncRef.current === nextSyncSignature) {
        lastAttemptedSkillMetadataSyncRef.current = null;
      }
      captureSentryException(caught);
    });
  }, [
    agentStore,
    attachedSkills,
    currentThreadMetadata,
    metadataAttachedSkillIds,
  ]);

  const handleCreateSkillFromChat = useCallback(() => {
    if (isLoading) {
      return;
    }

    setShouldAutoSubmit(false);
    agentStore.setError(null);
    setInput(buildCreateSkillFromChatAgentPrompt());
  }, [agentStore, isLoading, setInput, setShouldAutoSubmit]);

  return {
    attachedSkills,
    handleAttachedSkillsChange,
    handleCreateSkillFromChat,
    isCreateSkillFromChatDisabled: isLoading,
    resetAttachedSkillsForThreadChange,
  };
};
